import { useEffect, useMemo, useState } from 'react';
import { MetaPage, MetaUser } from '../types/meta.types';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { Search, Tags, FolderOpen, Layers3 } from 'lucide-react';
import ProjectInlineSelector from './ProjectInlineSelector';

/**
 * Modal pra gerenciar o projeto de cada página de uma conta Meta.
 *
 * - Lista todas as pages, com filtro por categoria + busca por nome.
 * - Seleção múltipla (incluindo "selecionar todas filtradas").
 * - Bulk apply: aplica um projeto a TODAS selecionadas (ou "herdar da conta").
 * - Também aceita override individual via dropdown inline em cada page.
 */

interface Props {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    auth: MetaUser | null;
    /** Pages dessa conta (já filtradas pelo pai). */
    pages: MetaPage[];
    projects: { id: number; name: string }[];
    /** Nome do projeto default da conta — usado como hint nas pages sem override. */
    accountProjectName: string | null;
    /** Aplica project_id=null pra "herdar da conta". */
    onUpdatePageProject: (pageId: number, projectId: number | null) => void;
}

const NONE = '__none__';
const CAT_ALL = '__all__';
const CAT_UNCATEGORIZED = '__none__';

export default function ManagePagesProjectsDialog({
    open,
    onOpenChange,
    auth,
    pages,
    projects,
    accountProjectName,
    onUpdatePageProject,
}: Props) {
    const [search, setSearch] = useState('');
    const [categoryFilter, setCategoryFilter] = useState<string>(CAT_ALL);
    const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
    const [bulkProject, setBulkProject] = useState<string>(NONE);

    // Reset state ao fechar / mudar de conta.
    useEffect(() => {
        if (!open) {
            setSearch('');
            setCategoryFilter(CAT_ALL);
            setSelectedIds(new Set());
            setBulkProject(NONE);
        }
    }, [open, auth?.id]);

    // Categorias presentes nas pages dessa conta.
    const categories = useMemo(() => {
        const set = new Set<string>();
        let hasUncategorized = false;
        pages.forEach(p => {
            if (p.internal_category_name) set.add(p.internal_category_name);
            else hasUncategorized = true;
        });
        const arr = Array.from(set).sort((a, b) => a.localeCompare(b, 'pt-BR'));
        if (hasUncategorized) arr.push(CAT_UNCATEGORIZED);
        return arr;
    }, [pages]);

    const filteredPages = useMemo(() => {
        const q = search.trim().toLowerCase();
        return pages.filter(p => {
            if (q) {
                const hay = `${p.page_name || ''} ${p.page_id || ''}`.toLowerCase();
                if (!hay.includes(q)) return false;
            }
            if (categoryFilter === CAT_ALL) return true;
            if (categoryFilter === CAT_UNCATEGORIZED) return !p.internal_category_name;
            return p.internal_category_name === categoryFilter;
        });
    }, [pages, search, categoryFilter]);

    const allFilteredSelected =
        filteredPages.length > 0 && filteredPages.every(p => selectedIds.has(p.id));
    const someFilteredSelected =
        filteredPages.some(p => selectedIds.has(p.id)) && !allFilteredSelected;

    const toggleAll = () => {
        setSelectedIds(prev => {
            const next = new Set(prev);
            if (allFilteredSelected) {
                filteredPages.forEach(p => next.delete(p.id));
            } else {
                filteredPages.forEach(p => next.add(p.id));
            }
            return next;
        });
    };

    const togglePage = (id: number) => {
        setSelectedIds(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const applyBulk = () => {
        if (!selectedIds.size) return;
        const projectId = bulkProject === NONE ? null : Number(bulkProject);
        selectedIds.forEach(pid => onUpdatePageProject(pid, projectId));
        // Limpa seleção pra evitar re-aplicar.
        setSelectedIds(new Set());
    };

    if (!auth) return null;

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-4xl bg-white rounded-2xl border-none shadow-2xl flex flex-col max-h-[85vh]">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2 text-xl font-bold text-[#0f172a]">
                        <FolderOpen className="h-5 w-5 text-indigo-600" />
                        Projetos das páginas
                    </DialogTitle>
                    <DialogDescription className="text-sm">
                        <span className="font-medium text-gray-800">{auth.meta_user_name}</span>
                        {' · '}
                        {accountProjectName ? (
                            <>Default da conta: <span className="font-medium">{accountProjectName}</span></>
                        ) : (
                            <span className="text-amber-600">Conta sem projeto</span>
                        )}
                    </DialogDescription>
                </DialogHeader>

                {/* Filtros */}
                <div className="flex flex-col sm:flex-row gap-2">
                    <div className="relative flex-1">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                        <Input
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Buscar por nome ou ID da página…"
                            className="pl-9 h-9 text-sm"
                        />
                    </div>
                    <div className="sm:w-64">
                        <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                            <SelectTrigger className="h-9 text-sm">
                                <Tags className="h-4 w-4 text-gray-400 mr-1" />
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value={CAT_ALL}>Todas categorias</SelectItem>
                                {categories.map(c => (
                                    <SelectItem key={c} value={c}>
                                        {c === CAT_UNCATEGORIZED ? '(sem categoria)' : c}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                </div>

                {/* Header de seleção + bulk apply */}
                <div className="flex items-center justify-between gap-3 px-3 py-2 bg-gray-50 rounded-lg border border-gray-100">
                    <label className="flex items-center gap-2 cursor-pointer text-sm">
                        <Checkbox
                            checked={allFilteredSelected ? true : someFilteredSelected ? 'indeterminate' : false}
                            onCheckedChange={toggleAll}
                        />
                        <span className="font-medium text-gray-700">
                            {selectedIds.size > 0
                                ? `${selectedIds.size} selecionada${selectedIds.size > 1 ? 's' : ''}`
                                : `Selecionar todas (${filteredPages.length})`}
                        </span>
                    </label>
                    <div className="flex items-center gap-2">
                        <span className="text-xs text-gray-500 hidden sm:block">Aplicar projeto:</span>
                        <Select value={bulkProject} onValueChange={setBulkProject}>
                            <SelectTrigger className="h-8 text-xs min-w-[180px]" disabled={!selectedIds.size}>
                                <SelectValue placeholder="Selecionar…" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value={NONE}>
                                    <span className="text-gray-500">
                                        Herdar da conta{accountProjectName ? ` · ${accountProjectName}` : ''}
                                    </span>
                                </SelectItem>
                                {projects.map(p => (
                                    <SelectItem key={p.id} value={String(p.id)}>{p.name}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        <Button
                            size="sm"
                            onClick={applyBulk}
                            disabled={!selectedIds.size}
                            className="h-8 bg-indigo-600 hover:bg-indigo-700 text-white"
                        >
                            <Layers3 className="h-3.5 w-3.5 mr-1" />
                            Aplicar
                        </Button>
                    </div>
                </div>

                {/* Lista */}
                <div className="flex-1 overflow-y-auto border border-gray-100 rounded-lg">
                    {filteredPages.length === 0 ? (
                        <div className="p-8 text-center text-sm text-gray-500">
                            Nenhuma página corresponde aos filtros.
                        </div>
                    ) : (
                        <ul className="divide-y divide-gray-100">
                            {filteredPages.map(p => {
                                const checked = selectedIds.has(p.id);
                                const hasOverride = p.project_id != null;
                                return (
                                    <li
                                        key={p.id}
                                        className={`flex items-center gap-3 px-3 py-2 hover:bg-gray-50 transition-colors ${checked ? 'bg-indigo-50/40' : ''}`}
                                    >
                                        <Checkbox checked={checked} onCheckedChange={() => togglePage(p.id)} />
                                        {p.picture_url ? (
                                            <img src={p.picture_url} alt={p.page_name} className="h-8 w-8 rounded object-cover flex-shrink-0" />
                                        ) : (
                                            <div className="h-8 w-8 rounded bg-gray-100 flex-shrink-0" />
                                        )}
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm font-medium text-gray-900 truncate">{p.page_name}</p>
                                            <div className="flex items-center gap-2 text-[10px] text-gray-500">
                                                <span className="truncate">{p.page_id}</span>
                                                {p.internal_category_name && (
                                                    <Badge variant="outline" className="text-[9px] px-1 py-0 h-4 bg-gray-50">
                                                        {p.internal_category_name}
                                                    </Badge>
                                                )}
                                                {hasOverride && (
                                                    <Badge variant="outline" className="text-[9px] px-1 py-0 h-4 text-indigo-700 border-indigo-200 bg-indigo-50">
                                                        override
                                                    </Badge>
                                                )}
                                            </div>
                                        </div>
                                        <ProjectInlineSelector
                                            value={p.project_id}
                                            onChange={(pid) => onUpdatePageProject(p.id, pid)}
                                            projects={projects}
                                            inheritHintName={accountProjectName}
                                            noneLabel="Sem projeto"
                                            className="h-8 text-xs w-[240px] flex-shrink-0"
                                        />
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </div>

                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)}>Fechar</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
